import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Upload, Trash2, Loader2, Package, FileText, CheckCircle2,
  RefreshCw, Filter, MapPin, X, ArrowRight, Boxes, Search as SearchIcon,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const LIST_COLORS = [
  "bg-sky-100 text-sky-900 border-sky-300",
  "bg-amber-100 text-amber-900 border-amber-300",
  "bg-violet-100 text-violet-900 border-violet-300",
  "bg-rose-100 text-rose-900 border-rose-300",
  "bg-emerald-100 text-emerald-900 border-emerald-300",
  "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  "bg-cyan-100 text-cyan-900 border-cyan-300",
  "bg-lime-100 text-lime-900 border-lime-300",
  "bg-orange-100 text-orange-900 border-orange-300",
  "bg-indigo-100 text-indigo-900 border-indigo-300",
];

export default function OndaReposicao() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [q, setQ] = useState("");
  const [rackFilter, setRackFilter] = useState("");
  const [status, setStatus] = useState("pendentes");
  const [bipTarget, setBipTarget] = useState(null); // {grupo, produto}
  const [bipLista, setBipLista] = useState(null);
  const [bipStatus, setBipStatus] = useState("REPOR");
  const [bipQtd, setBipQtd] = useState("");
  const [bipSaving, setBipSaving] = useState(false);
  const fileRef = useRef(null);

  const fetchOnda = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (rackFilter) params.rack = rackFilter;
      if (status && status !== "todos") params.status = status;
      const r = await api.get("/picklist/onda", { params });
      setData(r.data);
    } catch (e) {
      toast.error("Erro ao carregar onda consolidada");
    } finally {
      setLoading(false);
    }
  }, [q, rackFilter, status]);

  useEffect(() => { fetchOnda(); }, [fetchOnda]);

  const importarLote = async (files) => {
    if (!files || files.length === 0) return;
    if (files.length > 10) {
      toast.error("Máximo 10 listas por onda");
      return;
    }
    setImporting(true);
    try {
      const fd = new FormData();
      for (const f of files) fd.append("files", f);
      const r = await api.post("/picklist/onda/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.total} lista(s) iniciada(s) para processamento`);
      // aguarda o processamento async dos jobs e atualiza
      setTimeout(() => fetchOnda(), 2500);
      setTimeout(() => fetchOnda(), 5000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao importar lote");
    } finally {
      setImporting(false);
    }
  };

  const limparOnda = async () => {
    if (!window.confirm("Remover TODAS as listas da onda atual? Reposições em andamento permanecem.")) return;
    try {
      await api.delete("/picklist/onda");
      toast.success("Onda limpa");
      fetchOnda();
    } catch { toast.error("Erro ao limpar"); }
  };

  const abrirBip = (grupo, produto) => {
    setBipTarget({ grupo, produto });
    setBipLista(produto.listas[0]?.lista || null);
    setBipStatus("REPOR");
    setBipQtd("");
  };

  const confirmarBip = async () => {
    if (!bipTarget || !bipLista) return;
    if (bipStatus === "QTD" && (!bipQtd || Number(bipQtd) <= 0)) {
      toast.error("Informe uma quantidade válida");
      return;
    }
    setBipSaving(true);
    try {
      // Precisamos descobrir o endereço de SEPARAÇÃO (destino) dessa lista p/ esse código.
      // O endpoint /picklist/conferir espera endereço de destino na separação.
      const pl = await api.get("/picklist", { params: { lista: bipLista, page_size: 1000 } });
      let endereco_destino = null;
      for (const g of pl.data.grupos || []) {
        for (const p of g.produtos || []) {
          if (p.codigo === bipTarget.produto.codigo) {
            endereco_destino = g.endereco;
            break;
          }
        }
        if (endereco_destino) break;
      }
      if (!endereco_destino) {
        toast.error("Endereço de destino não encontrado na Lista " + bipLista);
        return;
      }
      await api.post("/picklist/conferir", {
        lista: bipLista,
        codigo: bipTarget.produto.codigo,
        endereco: endereco_destino,
        status: bipStatus,
        quantidade: bipStatus === "QTD" ? Number(bipQtd) : undefined,
      });
      toast.success(`Conferido na Lista ${bipLista}`);
      setBipTarget(null);
      fetchOnda();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao conferir");
    } finally {
      setBipSaving(false);
    }
  };

  const prog = data?.progresso || { conferidos: 0, total: 0, pct: 0 };
  const colorFor = (lista) => LIST_COLORS[(Number(lista) - 1) % LIST_COLORS.length] || LIST_COLORS[0];

  return (
    <div data-testid="onda-reposicao" className="space-y-3">
      {/* Header de controle */}
      <div className="bg-[#0F2A1F] text-white rounded-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-[#8DBDA2]">Onda de Reposição</div>
          <div className="text-lg font-bold">
            {data?.listas?.length || 0} lista(s) · {prog.total} itens para bipar · <span className="text-[#2DC26B]">{prog.pct}% conferidos</span>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".pdf" multiple className="hidden" data-testid="onda-import-input"
            onChange={(e) => { importarLote(e.target.files); e.target.value = ""; }} />
          <button data-testid="btn-importar-onda" onClick={() => fileRef.current?.click()} disabled={importing}
            className="h-10 px-4 bg-[#2DC26B] hover:bg-[#25A85B] disabled:opacity-60 text-white font-bold rounded-md flex items-center gap-2 text-sm">
            {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            Importar Lote (vários PDFs)
          </button>
          <button data-testid="btn-limpar-onda" onClick={limparOnda} className="h-10 px-3 bg-white/10 hover:bg-white/20 text-white rounded-md flex items-center gap-1 text-sm">
            <Trash2 className="w-4 h-4" /> Limpar Onda
          </button>
        </div>
      </div>

      {/* Chips de listas na onda */}
      {data?.listas?.length > 0 && (
        <div className="bg-white border border-[#D9E3DC] rounded-lg p-3 flex flex-wrap gap-2" data-testid="onda-chips-listas">
          {data.listas.map((l) => (
            <div key={l.lista} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded border text-xs font-semibold ${colorFor(l.lista)}`} data-testid={`onda-chip-lista-${l.lista}`}>
              <FileText className="w-3 h-3" />
              Lista {l.lista}: <span className="font-mono">{l.total_itens || 0} itens / {l.total_unidades || 0} un</span>
              {l.lote && <span className="opacity-70">· Lote {l.lote}</span>}
            </div>
          ))}
        </div>
      )}

      {/* Filtros */}
      <div className="bg-white border border-[#D9E3DC] rounded-lg p-3 grid md:grid-cols-6 gap-2">
        <div className="relative md:col-span-3">
          <SearchIcon className="w-4 h-4 text-[#8A9A90] absolute left-3 top-1/2 -translate-y-1/2" />
          <input data-testid="onda-busca" value={q} onChange={(e) => setQ(e.target.value)} className="w-full h-10 pl-9 pr-3 border border-[#D9E3DC] rounded-md text-sm" placeholder="Buscar código ou descrição…" />
        </div>
        <select data-testid="onda-filtro-rack" value={rackFilter} onChange={(e) => setRackFilter(e.target.value)} className="h-10 px-2 border border-[#D9E3DC] rounded-md text-sm">
          <option value="">Todos racks</option>
          {(data?.racks_pulmao || []).map((r) => <option key={r} value={r}>Rack {r}</option>)}
        </select>
        <select data-testid="onda-filtro-status" value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 px-2 border border-[#D9E3DC] rounded-md text-sm">
          <option value="pendentes">Pendentes</option>
          <option value="OK">Conferidos (OK)</option>
          <option value="REPOR">Marcados REPOR</option>
          <option value="todos">Todos</option>
        </select>
        <button data-testid="onda-refresh" onClick={fetchOnda} className="h-10 px-3 bg-[#0F6B3A] hover:bg-[#0B5530] text-white rounded-md font-semibold text-sm flex items-center justify-center gap-1">
          <RefreshCw className="w-4 h-4" /> Atualizar
        </button>
      </div>

      {/* Lista agrupada por rack */}
      {loading && (
        <div className="text-center text-slate-400 py-8" data-testid="onda-loading">
          <Loader2 className="w-6 h-6 animate-spin inline-block" />
        </div>
      )}
      {!loading && (!data?.grupos || data.grupos.length === 0) && (
        <div className="bg-white border border-dashed border-[#D9E3DC] rounded-lg p-10 text-center text-[#8A9A90]" data-testid="onda-vazia">
          <Boxes className="w-10 h-10 mx-auto opacity-30 mb-2" />
          {data?.listas?.length === 0 ? "Importe listas (PDF) para montar sua onda" : "Nenhum item encontrado com os filtros atuais"}
        </div>
      )}
      {!loading && data?.grupos?.length > 0 && (
        <div className="space-y-3">
          {data.grupos.map((g, gi) => (
            <div key={gi} className="bg-white border border-[#D9E3DC] rounded-lg overflow-hidden" data-testid={`onda-grupo-${g.rack}-${g.linha}-${g.coluna}`}>
              <div className="bg-[#F1F5F2] border-b border-[#D9E3DC] px-3 py-2 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#0F6B3A]" />
                <div className="font-mono font-bold text-[#0F2A1F]">{g.endereco}</div>
                <span className="text-xs text-[#5A6B62]">Rack {g.rack} · Linha {g.linha} · Col {g.coluna}</span>
                <span className="ml-auto text-xs font-semibold text-[#5A6B62]">{g.produtos.length} produto(s)</span>
              </div>
              <div className="divide-y divide-[#F1F5F2]">
                {g.produtos.map((p, pi) => (
                  <div key={pi} className="px-3 py-2 flex flex-wrap items-center gap-3" data-testid={`onda-item-${p.codigo}`}>
                    <div className="min-w-[60px]">
                      <div className="font-mono font-bold text-[#0F2A1F]">{p.codigo}</div>
                    </div>
                    <div className="flex-1 min-w-[180px]">
                      <div className="text-sm text-[#0F2A1F] truncate" title={p.nome}>{p.nome}</div>
                      <div className="text-[10px] text-[#8A9A90]">
                        Pulmão disp.: {p.qtd_pulmao} un
                      </div>
                    </div>
                    <div className="flex items-center gap-1 flex-wrap">
                      {p.listas.map((l) => {
                        const st = p.status_por_lista[l.lista];
                        const feito = !!st;
                        return (
                          <span
                            key={l.lista}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-semibold ${colorFor(l.lista)} ${feito ? "opacity-40 line-through" : ""}`}
                            title={feito ? `Lista ${l.lista} - ${st}` : `Lista ${l.lista}: ${l.qtd} un`}
                          >
                            L{l.lista}:{l.qtd}
                            {feito && <CheckCircle2 className="w-3 h-3" />}
                          </span>
                        );
                      })}
                    </div>
                    <div className="font-mono text-sm text-[#0F2A1F] font-bold">
                      Total: {p.qtd_total}
                    </div>
                    <button
                      data-testid={`btn-bipar-${p.codigo}`}
                      onClick={() => abrirBip(g, p)}
                      className="h-9 px-3 bg-[#2DC26B] hover:bg-[#25A85B] text-white font-semibold rounded-md text-xs flex items-center gap-1"
                    >
                      Bipar <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Dialog Bip — pergunta qual lista */}
      <Dialog open={!!bipTarget} onOpenChange={(v) => !v && setBipTarget(null)}>
        <DialogContent className="max-w-md" data-testid="modal-bip-onda">
          <DialogHeader>
            <DialogTitle>Conferir item</DialogTitle>
            <DialogDescription>
              {bipTarget && (
                <span>
                  <b>{bipTarget.produto.codigo}</b> — {bipTarget.produto.nome}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {bipTarget && (
            <div className="space-y-3 py-1">
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">Qual lista está sendo separada agora?</div>
                <div className="flex flex-wrap gap-2">
                  {bipTarget.produto.listas.map((l) => {
                    const feito = !!bipTarget.produto.status_por_lista[l.lista];
                    const ativa = bipLista === l.lista;
                    return (
                      <button
                        key={l.lista}
                        data-testid={`bip-lista-${l.lista}`}
                        disabled={feito}
                        onClick={() => setBipLista(l.lista)}
                        className={`px-3 py-2 rounded-md border text-sm font-semibold ${
                          feito ? "opacity-40 line-through cursor-not-allowed" :
                          ativa ? "bg-[#2DC26B] border-[#2DC26B] text-white" :
                          "bg-white border-slate-300 hover:bg-slate-50"
                        }`}
                      >
                        Lista {l.lista} · {l.qtd} un {feito && "(já conferido)"}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs font-semibold text-slate-600 mb-1">Status da conferência</div>
                <div className="flex gap-2">
                  {["OK", "REPOR", "QTD"].map((s) => (
                    <button
                      key={s}
                      data-testid={`bip-status-${s}`}
                      onClick={() => setBipStatus(s)}
                      className={`px-3 py-2 rounded-md text-xs font-bold ${
                        bipStatus === s ? "bg-[#0F6B3A] text-white" : "bg-slate-100 text-slate-700"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              {bipStatus === "QTD" && (
                <div>
                  <div className="text-xs font-semibold text-slate-600 mb-1">Quantidade encontrada</div>
                  <input type="number" value={bipQtd} onChange={(e) => setBipQtd(e.target.value)}
                    className="w-full h-10 px-3 border border-slate-300 rounded-md"
                    data-testid="bip-qtd" />
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setBipTarget(null)} className="h-9 px-3 bg-slate-100 hover:bg-slate-200 rounded-md text-sm">Cancelar</button>
            <button data-testid="btn-confirmar-bip" onClick={confirmarBip} disabled={bipSaving || !bipLista}
              className="h-9 px-4 bg-[#2DC26B] hover:bg-[#25A85B] disabled:opacity-60 text-white font-bold rounded-md text-sm flex items-center gap-1">
              {bipSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              Confirmar
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
